import React from "react";

import { AssetTypes } from "common/utils/types";
import { getInputDateFormat } from "../utils/utility";
import { SelectOptions } from "../assets/data/options";
import { Token } from "common/store/token/types";
import { User } from "../store/user/types";
import { useTranslation } from "react-i18next";

import {
  CommonSelect,
  CommonTextField,
  CommonDatePicker,
} from "./index";

export const TokenInfoField = ({
  id,
  values,
  errors,
  touched,
  setFieldValue,
  disableImmutable,
  className,
  disabled,
  noLabels,
}: any) => {
  //Hooks
  const { t, i18n } = useTranslation();

  //Helpers
  let loanOption = SelectOptions()['asset'].find(option => option.value === AssetTypes.LOAN);
  let realEstateOption = SelectOptions()['asset'].find(option => option.value === AssetTypes.REAL_ESTATE);

  const shouldDisable = (key: string) => {
    let disabledById = false;

    switch (key) {
      case "interest":
      case "LTV":
        disabledById = !!realEstateOption && values.asset_type === realEstateOption.value;
        break;
      case "location":
        disabledById = !!loanOption && values.asset_type === loanOption.value;
        break;
    }

    return disabled || disabledById || (disableImmutable && !User.updateFields.includes(key));
  };

  const getCommonDatePickerWrapperClass = (key: string) => {
    const isValidationFailed = errors && touched && errors[key] && touched[key];
    const isEmpty = values[key] ? false : true;
    return (
      "common-date-picker" +
      (isValidationFailed
        ? "--error"
        : shouldDisable(key)
        ? "--disabled"
        : "") +
      " common-date-picker" +
      (isEmpty ? "--empty" : "")
    );
  };

  const getPlaceholder = (id: string) => {
    if (noLabels) {
      return undefined;
    }

    switch (id) {
      case "asset_type":
        return t("Select...");
      case "cap":
        return t("Supply Cap");
      case "company":
        return t("Company");
      case "description":
        return t("Description");
      case "interest":
        return t("Interest");
      case "location":
        return t("Location");
      case "LTV":
        return t("LTV (%)");
      case "max":
        return t("Maximum Purchase");
      case "min":
        return t("Minimum Purchase");
      case "name":
        return t("Name");
      case "price":
        return t("Price (€)");
      case "symbol":
        return t("Symbol");
      case "value":
        return t("Asset value (€)");
      default:
        return undefined;
    }
  }

  const getInitialFocusedDate = (id: string) => {
    switch (id) {
      default:
        return undefined;
    }
  }

  const getInputProps = (id: string) => {
    switch (id) {
      case "cap":
        return {
          min: Token.minCap,
        };
      case "interest":
        return {
          min: Token.minInterest,
        };
      case "LTV":
        return {
          min: Token.minLTV,
        };
      case "max":
        return {
          min: Token.minMax,
        };
      case "min":
        return {
          min: Token.minMin,
        };
      case "price":
        return {
          min: Token.minPrice,
        };
      case "value":
        return {
          min: Token.minValue,
        };
      default:
        return undefined;
    }
  }

  const getLabel = (id: string) => {
    if (noLabels) {
      return undefined;
    }

    switch (id) {
      case "asset_type":
        return t("Asset type");
      case "end":
        return t("Sale end");
      case "start":
        return t("Sale start");
      default:
        return undefined;
    }
  }

  const getOptions = (id: string) => {
    switch (id) {
      case "asset_type":
        return SelectOptions()["asset"];
      default:
        return undefined;
    }
  }

  const getType = (id: string) => {
    switch (id) {
      case "cap":
      case "interest":
      case "LTV":
      case "max":
      case "min":
      case "price":
      case "value":
        return "number";
      default:
        return undefined;
    }
  }

  const getMaxDate = (id: string) => {
    switch (id) {
      case "start":
        if (!!values.end) {
          let maxDate = new Date(values.end);
          maxDate.setDate(maxDate.getDate() - 1);
          return maxDate;
        } else {
          return undefined;  
        }
      default:
        return undefined;
    }
  }

  const getMinDate = (id: string) => {
    switch (id) {
      case "end":
        if (!!values.start) {
          let minDate = new Date(values.start);
          minDate.setDate(minDate.getDate() + 1);
          return minDate;
        } else {
          return undefined;  
        }
      default:
        return undefined;
    }
  }

  const getSetFieldValueWrapper = (id: string) => {
    switch (id) {
      case "cap":
      case "interest":
      case "LTV":
      case "max":
      case "min":
      case "price":
      case "value":
        return (id: string, value: number) => setFieldValue(id, Math.round(value))
      default:
        return setFieldValue;
    }
  }

  const shouldDisableFuture = (id: string) => {
    switch (id) {
      default:
        return false;
    }
  }

  const shouldDisablePast = (id: string) => {
    switch (id) {
      case "start":
      case "end":
        return true;
      default:
        return false;
    }
  }

  //Options
  const disableFuture = shouldDisableFuture(id);
  const disablePast = shouldDisablePast(id);
  const initialFocusedDate = getInitialFocusedDate(id);
  const inputProps = getInputProps(id);
  const label = getLabel(id);
  const maxDate = getMaxDate(id);
  const minDate = getMinDate(id);
  const placeholder = getPlaceholder(id);
  const options = getOptions(id);
  const type = getType(id);
  const setFieldValueWrapper = getSetFieldValueWrapper(id);

  switch (id) {
    case "cap":
    case "company":
    case "description":
    case "interest":
    case "location":
    case "LTV":
    case "max":
    case "min":
    case "name":
    case "price":
    case "symbol":
    case "value":
      return (
        <CommonTextField
          disabled={shouldDisable(id)}
          id={id}
          type={type}
          label={label}
          placeholder={placeholder}
          values={values}
          errors={errors}
          touched={touched}
          setFieldValue={setFieldValueWrapper}
          className={className}
          inputProps={inputProps}
        />
      );
    case "asset_type":
      return (
        <CommonSelect
          disabled={shouldDisable(id)}
          id={id}
          label={label}
          placeholder={placeholder}
          values={values}
          errors={errors}
          touched={touched}
          setFieldValue={setFieldValue}
          options={options}
          className={className}
        />
      );
    case "start":
    case "end":
      return (
        <CommonDatePicker
          className={className + " " + getCommonDatePickerWrapperClass(id)}
          disabled={shouldDisable(id)}
          id={id}
          values={values}
          errors={errors}
          touched={touched}
          label={label}
          setFieldValue={setFieldValue}
          maxDate={maxDate}
          minDate={minDate}
          disableFuture={disableFuture}
          disablePast={disablePast}
          openTo="year"
          views={["year", "month", "date"]}
          format={getInputDateFormat(i18n.language)}
          initialFocusedDate={initialFocusedDate}
        />
      );
    default:
      return <></>;
  }
};
